import { useEffect } from 'react';
import { Image } from '../../../types/api/ImageType';
import { useFormContext } from '../../../components/molecules/Form';
import { isEmptyArray, isNonEmptyArray } from '../../../lib/typeGuards';
import IConstraints from '../../../types/IContraints';
import { useImageUpload } from '../../FileUpload';

const getModelValue = (modelValue: [] | [File] | Image | undefined): File | Image | undefined => {
  if (Array.isArray(modelValue) && isNonEmptyArray(modelValue)) {
    return modelValue[0];
  }
  if (Array.isArray(modelValue) && isEmptyArray(modelValue)) {
    return undefined;
  }

  return modelValue;
};

export function useSingleImageUpload<T extends Image = Image>(id: string, clearValue?: boolean) {
  const { inputs } = useFormContext();
  const { imageuploadparams } = inputs as any;
  const modelValue = imageuploadparams.params.image.value as T | [] | [File] | undefined;
  const imageInModel = getModelValue(modelValue);

  const { image } = imageuploadparams.params;
  const constraints = image.constraints as IConstraints;
  const { selection, ...rest } = useImageUpload({
    input: image,
    id,
    clearValue,
    onChange: files =>
      imageuploadparams.onChange(
        image.name,
        files.filter(({ errors }) => isEmptyArray(errors)).map(f => f.file),
      ),
  });

  const selectedPreview = selection.length > 0 ? selection[0] : undefined;
  const error = selectedPreview?.errors;

  useEffect(() => {
    if (error) {
      rest.setValidity(error);
    }
    return () => {
      rest.setValidity('');
    };
  }, [error, rest]);

  return {
    ...rest,
    selectedPreview,
    constraints,
    imageInModel,
    suspendPreviewProps: { id, selectedPreview, isLoading: rest.isReadingFiles },
    imageUploadParamsProps: { id, selectedPreview },
  };
}
